<template>
    <div class="orders-page">
        <slot name="breadcrumbs">
            <breadcrumbs v-if="!isMobile" class="mb-8" />
            <page-title :text="$t('page_titles.orders_page_title')" />
        </slot>
        <teleport v-if="isMobile && !companyPageMode" to="#under-header">
            <v-tabs v-model="selectedTab" :stacked="isMobile" fixed-tabs class="sticky-bar">
                <v-tab v-for="tab in tabs" :key="tab.label">
                    {{ isMobile ? $t(`${tab.label}`) : '' }}
                    <v-icon>{{ tab.icon }}</v-icon>
                </v-tab>
            </v-tabs>
        </teleport>
        <v-row dense class="mt-8 mt-md-0">
            <v-col
                v-if="(!isMobile || selectedTab === 0) && !isSupplier && personRole !== 'dispatcher'"
                class="orders-page__book-col"
                cols="12"
                sm="4"
                md="3"
            >
                <slot name="bookButtons" :new-order-request-modal="newOrderRequestModal">
                    <v-btn class="bg-primary" size="small" to="/">
                        {{ $t('new_order') }}
                    </v-btn>
                    <v-btn
                        v-if="isShowNewReservationRequest"
                        size="small"
                        class="bg-grey-lighten-4 ms-2"
                        @click="newOrderRequestModal = true"
                    >
                        {{ $t('new_reservation_request') }}
                    </v-btn>
                </slot>
                <new-order-request-modal ref="newOrderRequestModal" v-model="newOrderRequestModal" />
            </v-col>
            <v-col v-if="(!isMobile || selectedTab === 1) && !companyPageMode" cols="12" md="2">
                <order-quick-filter ref="quickFilter" :person="person" @change="changeQuickFilter" />
            </v-col>
        </v-row>
        <div v-show="!isMobile || selectedTab === 1">
            <order-selected-filters v-if="!companyPageMode" @filter="filterOrderSelectedFilters">
                <template v-slot:save-btn>
                    <custom-filter-modal
                        v-if="isSomeFiltersSelected"
                        :filters="ordersFilters"
                        :person="person"
                        @create-custom-filter="$refs.quickFilter.loadCustomFilters()"
                    />
                </template>
            </order-selected-filters>
            <order-filters
                ref="filters"
                :filters-list="filtersList"
                :hidden-filters="hiddenFilters"
                :company-page-mode="companyPageMode"
                :product-page-mode="productPageMode"
                @change-filters="hiddenFilters = {...hiddenFilters, filters: $event}"
                @filter="filterOrderFilters"
                @reset-filters="onFiltersReset"
            />
        </div>
        <div v-show="!isMobile || selectedTab === 0">
            <div v-if="!isMobile" class="orders-page__pre-table">
                <v-divider />
                <div class="d-flex align-center" style="min-height: 40px">
                    <div class="d-flex align-center">
                        <div v-if="ordersCount" class="me-2 text-grey text-caption d-flex flex-wrap">
                            <span class="nowrap">
                                {{ `${$t('orders')}: ${ordersCount}` }}
                                &nbsp;
                            </span>
                            <span class="nowrap">
                                {{ `(${$t('services')}: ${servicesCount})` }}
                            </span>
                        </div>
                        <template v-if="isB2B">
                            <orders-download-menu :items="selectedOrders" :sort-order="sortOrder" class="d-block" />
                            <order-emails-menu v-if="showOrderEmailsMenu" v-model="selectedOrders" class="d-block" />
                            <slot name="agentsPaymentsUpload">
                                <agents-payments-upload v-if="isTO1" />
                            </slot>
                            <edit-reservations-modal
                                v-if="(isTourOperator || isAgency) && $conf.public.showEditeReservations"
                                :order-ids="selectedOrderIds"
                                :campaigns="campaigns"
                                @save="filterOrderFilters"
                            />
                            <reservations-summary-modal v-if="isShowReservationsSummaryModal" :filter-key="filterKey" />
                            <issue-bulk-manual-invoices-menu
                                v-if="isTO1 && $conf.public.account.paymentScheduler && !companyPageMode"
                                :selected-object-ids="selectedObjectIds"
                                :filter-key="filterKey"
                                :invoice-type="'SUPPLIER'"
                                :label="$t('receive_supplier_invoices')"
                            />
                            <issue-bulk-invoices-menu
                                v-if="isTO1 && !$conf.public.account.paymentScheduler && !companyPageMode"
                                :selected-object-ids="selectedObjectIds"
                                :filter-key="filterKey"
                                :label="$t('receive_revise_act')"
                            />
                        </template>
                    </div>
                    <v-spacer />
                    <template v-if="isB2B">
                        <cancelled-services-checkbox v-if="showCancelledServicesCheckbox" class="me-4" />
                        <show-commission-and-netto-checkbox
                            v-show="(isTourOperator || isAgency) && personRole !== 'dispatcher'"
                            class="me-8"
                        />
                        <div class="px-4">
                            <v-switch
                                v-model="activeFilter"
                                color="primary"
                                density="compact"
                                hide-details
                                class="mt-0 nowrap"
                                @update:model-value="$refs.filters.filter()"
                            >
                                <template v-slot:label>
                                    <span class="text-no-wrap">
                                        {{ activeFilter ? $t('active') : $t('orderStatus.ARCHIVED') }}
                                    </span>
                                </template>
                            </v-switch>
                        </div>
                        <reservations-table-options-modal
                            :table-columns="tableColumns"
                            class="me-2"
                            @change="hiddenColumns.columns = $event"
                        />
                    </template>
                    <v-btn-toggle v-model="expandedAllButtonGroup" class="text-right" variant="flat" mandatory>
                        <v-btn data-cy="expand-all-button" @click="expandAll">
                            <v-icon>mdi-arrow-expand-vertical</v-icon>
                        </v-btn>
                        <v-btn data-cy="hide-all-button" @click="collapseAll">
                            <v-icon>mdi-format-align-justify</v-icon>
                        </v-btn>
                    </v-btn-toggle>
                </div>
            </div>
            <v-row>
                <v-col cols="12">
                    <orders
                        ref="orders"
                        v-model="selectedOrders"
                        :is-expanded-all="!expandedAllButtonGroup"
                        :selectable="selectable"
                        :table-columns="tableColumns"
                        @change-sort-order="sortOrder = $event"
                    />
                </v-col>
            </v-row>
        </div>
        <assistant-chat
            v-if="$conf.public.openAI.showAssistant"
            :assistant-id="$conf.public.openAI.ordersPageAssistantKey"
            :instructions="assistantInstructions"
            page="orders"
        />
    </div>
</template>

<script lang="ts">
    import {Component, mixins, Prop, Watch} from 'vue-facing-decorator'
    import Breadcrumbs from '~src/components/Breadcrumbs.vue'
    import Orders from '~src/components/account/orders.src'
    import OrderSelectedFilters from '@/components/account/filters/OrderSelectedFilters.vue'
    import OrderQuickFilter from '@/components/account/filters/OrderQuickFilter.vue'
    import CustomFilterModal from '@/components/account/filters/CustomFilterModal.vue'
    import OrderFilters from '~src/components/account/filters/orderFilters.src'
    import NewOrderRequestModal from '~src/components/account/modals/newOrderRequest/newOrderRequestModal.src'
    import ShowCommissionAndNettoCheckbox from '~src/components/account/showCommissionAndNettoCheckbox.src'
    import CancelledServicesCheckbox from '@/components/account/CancelledServicesCheckbox.vue'
    import ReservationsTableOptionsModal from '@/components/account/ReservationsTableOptionsModal.vue'
    import OrdersDownloadMenu from '~src/components/account/snippets/OrdersDownloadMenu.vue'
    import OrderEmailsMenu from '@/components/account/snippets/OrderEmailsMenu.vue'
    import AgentsPaymentsUpload from '~src/components/account/snippets/AgentsPaymentsUpload.vue'
    import {AuthInfoMixin} from '~src/mixins/authInfoMixin.src'
    import {persistentStore, runtimeStore} from '~/composables/store-accessor'
    import EditReservationsModal from '@/components/account/EditReservationsModal.vue'
    import ReservationsSummaryModal from '@/components/account/ReservationsSummaryModal.vue'
    import IssueBulkManualInvoicesMenu from '~/components/account/IssueBulkManualInvoicesMenu.vue'
    import IssueBulkInvoicesMenu from '~/components/account/IssueBulkInvoicesMenu.vue'
    import AssistantChat from '@/components/openAI/AssistantChat.vue'
    import FileDownloadModal from '~/components/account/snippets/FileDownloadModal.vue'
    import PageTitle from '~/components/parts/PageTitle.vue'
    import type {TableHeadersType} from '~/utils/types/types'

    // TODO [P1] Need check
    @Component({
        components: {
            FileDownloadModal,
            AgentsPaymentsUpload,
            ShowCommissionAndNettoCheckbox,
            NewOrderRequestModal,
            Orders,
            Breadcrumbs,
            OrderSelectedFilters,
            OrderFilters,
            OrderQuickFilter,
            CustomFilterModal,
            CancelledServicesCheckbox,
            ReservationsTableOptionsModal,
            OrdersDownloadMenu,
            OrderEmailsMenu,
            EditReservationsModal,
            ReservationsSummaryModal,
            AssistantChat,
            IssueBulkManualInvoicesMenu,
            IssueBulkInvoicesMenu,
            PageTitle,
        },
    })
    export class OrdersPage extends mixins(AuthInfoMixin) {
        @Prop({default: false, type: Boolean}) companyPageMode: boolean
        @Prop({default: false, type: Boolean}) productPageMode: boolean

        expandedAllButtonGroup = 0
        newOrderRequestModal: boolean = false
        selectedTab: number = 0
        hiddenColumns = {
            columns: [],
            userId: null,
        }
        hiddenFilters = {
            filters: [],
        }
        campaigns = []

        selectedOrders = []
        sortOrder = 'none'

        assistantInstructions =
            'You are a helpful assistant. Your goal is to help users find information about orders (or reservations) from the uploaded files.\n' +
            'Start the conversation with a greeting.'

        @Watch('newOrderRequestModal')
        onNewOrderRequestModalChange() {
            if (this.newOrderRequestModal === false) {
                this.$emit('new-order-request-close')
            }
        }

        onFiltersReset() {
            this.selectedOrders = []
        }

        async created() {
            await this.getHiddenColumns()
            await this.getHiddenFilters()
            if (!this.productPageMode && persistentStore.orderFilters.productId) {
                persistentStore.SET_ORDER_FILTER({key: 'productId', val: null})
                persistentStore.SET_ORDER_FILTER({key: 'filterServices', val: false})
            }
        }

        async mounted() {
            await this.$store.restored
            if (this.isB2B) {
                this.expandedAllButtonGroup = this.$conf.public.isOrderListExpandedByDefault ? 0 : 1
                if (this.$conf.public.isOrderListExpandedByDefault) {
                    this.$refs.orders.expandAll()
                }
            } else if (this.isB2C) {
                this.expandedAllButtonGroup = this.$conf.public.isOrderListExpandedByDefault ? 0 : 1
                if (this.$conf.public.isOrderListExpandedByDefaultB2C) {
                    this.$refs.orders.expandAll()
                }
            }

            if (persistentStore.selectedOrderId) {
                persistentStore.SET_SELECTED_ORDER_ID(null)
            }

            if (this.isTO1 && (this.personRole === 'director' || this.personRole === 'supervisor')) {
                try {
                    const {campaigns} = await this.$api.campaigns.get()
                    this.campaigns = campaigns.map(({campaignId, name}) => ({value: campaignId, title: name}))
                } catch (e) {
                    this.campaigns = []
                }
            }
        }

        async getHiddenColumns() {
            try {
                const res = await this.$api.reservationsTableOptions.get({userId: authStore.person.personId})
                if (res?.length) this.hiddenColumns = res[0]
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        async getHiddenFilters() {
            try {
                const res = await this.$api.reservationsFiltersConfiguration.get({
                    userId: authStore.person.personId,
                })
                if (res?.length) this.hiddenFilters = res[0]
                // eslint-disable-next-line no-empty
            } catch (e) {}
        }

        collapseAll() {
            this.$refs.orders.collapseAll()
            this.expandedAllButtonGroup = 1
        }

        expandAll() {
            this.$refs.orders.expandAll()
            this.expandedAllButtonGroup = 0
        }

        changeQuickFilter() {
            this.$refs.orders.filter()
            this.$refs.filters.load()
            this.selectedTab = 0
        }

        filterOrderSelectedFilters($event) {
            this.$refs.orders.filter()
            this.$refs.filters.load()
            this.$refs.filters.clearTripsFilter($event)
            this.$refs.quickFilter.clearQuickFilter()
        }

        async filterOrderFilters() {
            this.selectedOrders = []
            await this.$refs.orders.filter()
            if (persistentStore.orderFilters.filterServices) this.expandAll()
            this.selectedTab = 0
        }

        get selectedOrderIds() {
            return this.selectedOrders.reduce((orderIds, order) => {
                if (order.orderId) orderIds.push(order.orderId)

                return orderIds
            }, [])
        }

        get selectedObjectIds() {
            return this.selectedOrders.map(o => o.objectId)
        }

        get ordersFilters() {
            return persistentStore.orderFilters
        }

        get activeFilter() {
            return this.ordersFilters.active
        }

        set activeFilter(val) {
            persistentStore.SET_ORDER_FILTER({key: 'active', val})
        }

        get isSomeFiltersSelected() {
            const filtersCopy = {...this.ordersFilters}

            for (const key in filtersCopy) {
                if (Array.isArray(filtersCopy[key])) {
                    if (filtersCopy[key].length > 0) {
                        return true
                    }
                } else if (filtersCopy[key] instanceof String) {
                    if (filtersCopy[key].length > 0) {
                        return true
                    }
                } else if (filtersCopy[key]) {
                    return true
                }
            }

            return false
        }

        get filtersList() {
            let list = [
                {
                    value: 'orderId',
                    label: this.$t('order_number'),
                    show: true,
                },
                {
                    value: 'travelerName',
                    label: this.$t('traveler_name'),
                    show: true,
                },
                {
                    value: 'processStatuses',
                    label: this.$t('services_status'),
                    show: true,
                },
                {
                    value: 'filterServices',
                    label: this.$t('apply_filter_to_services'),
                    show: true,
                },
                {
                    value: 'createdDate',
                    label: this.$t('create_date'),
                    show: true,
                },
                {
                    value: 'modifiedDate',
                    label: this.$t('modified_date'),
                    show: true,
                },
                {
                    value: 'serviceStartDate',
                    label: this.$t('service_start_date'),
                    show: true,
                },
                {
                    value: 'serviceEndDate',
                    label: this.$t('service_end_date'),
                    show: true,
                },
                {
                    value: 'serviceTypes',
                    label: this.$t('service_types'),
                    show: true,
                },
                {
                    value: 'serviceName',
                    label: this.$t('service_name'),
                    show: true,
                },
                {
                    value: 'city',
                    label: this.$t('city'),
                    show: true,
                },
            ]

            if (this.$conf.public.account.showBookingDate) {
                list.push({
                    value: 'manualBookingDate',
                    label: this.$t('booking_date'),
                    show: true,
                })
            }

            if (this.isTO1) {
                list.push({
                    value: 'serviceSources',
                    label: this.$t('source_of_service'),
                    show: true,
                })
                list.push({
                    value: 'bookingMethod',
                    label: this.$t('booked_from'),
                    show: true,
                })
                list.push({
                    value: 'customerPaymentDueDate',
                    label: this.$t('customer_payment_due_date'),
                    show: true,
                })
                list.push({
                    value: 'supplierPaymentDueDate',
                    label: this.$t('supplier_payment_due_date'),
                    show: true,
                })
                if (this.$conf.public.reservationFilters.departments) {
                    list.push({
                        value: 'departmentId',
                        label: this.$t('department'),
                        show: true,
                    })
                    list.push({
                        value: 'serviceDepartmentId',
                        label: this.$t('product_department'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.serviceManager) {
                    list.push({
                        value: 'serviceManager',
                        label: this.$t('service_manager'),
                        show: true,
                    })
                }
                list.push({
                    value: 'classification',
                    label: this.$t('filterTitles.classifications'),
                    show: true,
                })
            }

            if (this.isTO1 && (this.personRole === 'director' || this.personRole === 'supervisor')) {
                list.push({
                    value: 'campaigns',
                    label: this.$t('campaigns'),
                    show: true,
                })
            }

            if (this.optionOrdersEnabled || this.activeProducts.includes('flights')) {
                list.push({
                    value: 'lastTicketingDate',
                    label: this.$t('last_ticketing_date'),
                    show: true,
                })
            }
            if (this.activeProducts.includes('flights')) {
                list.push({
                    value: 'ticketNumber',
                    label: this.$t('ticket_number'),
                    show: true,
                })
            }
            if (!this.isAgency && this.$conf.public.reservationFilters.reservationStatus) {
                list.push({
                    value: 'reservationStatuses',
                    label: this.$t('reservation_status'),
                    show: true,
                })
            }
            if (!this.isSupplier && this.$conf.public.reservationFilters.reservationName) {
                list.push({
                    value: 'reservationName',
                    label: this.$t('reservation_title'),
                    show: true,
                })
            }
            if (!this.isB2C && !this.isSupplier) {
                list.push({
                    value: 'reservationManager',
                    label: this.$t('reservation_manager'),
                    show: true,
                })
                if (this.$conf.public.reservationFilters.promoCode) {
                    list.push({
                        value: 'promoCode',
                        label: this.$t('promocode'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.loyaltyPoints) {
                    list.push({
                        value: 'loyaltyPoints',
                        label: this.$t('loyalty_points'),
                        show: true,
                    })
                }
            }
            if (this.$conf.public.reservationFilters.waitingApprovalFromMyself && (this.isAgency || this.isCorporate)) {
                list.push({
                    value: 'waitingApprovalFromMyself',
                    label: this.$t('waiting_approval_from_myself'),
                    show: true,
                })
            }
            if (this.isTourOperator || this.isAgency || this.isCorporate) {
                if (this.$conf.public.reservationFilters.corporatePolicyViolated) {
                    list.push({
                        value: 'corporatePolicyViolated',
                        label: this.$t('corporate_policy_violation'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.waitAuthorize) {
                    list.push({
                        value: 'waitAuthorize',
                        label: this.$t('approval_required'),
                        show: true,
                    })
                }
            }
            if (this.isTourOperator) {
                list.push({
                    value: 'agentCompanyConsortium',
                    label: this.$t('agent_consortium'),
                    show: true,
                })
                list.push({
                    value: 'agentCompanySubconsortium',
                    label: this.$t('agent_sub_consortium'),
                    show: true,
                })
                list.push({
                    value: 'paymentMethod',
                    label: this.$t('payment_method'),
                    show: true,
                })
                list.push({
                    value: 'client',
                    label: this.$t('client_name'),
                    show: true,
                })
                list.push({
                    value: 'customerCompany',
                    label: this.$t('customer_company'),
                    show: true,
                })
                list.push({
                    value: 'agent',
                    label: this.$t('agent_name'),
                    show: true,
                })
                if (this.$conf.public.reservationFilters.clientType) {
                    list.push({
                        value: 'clientType',
                        label: this.$t('client_type'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.tripOrganizer) {
                    list.push({
                        value: 'tripOrganizer',
                        label: this.$t('trip_organizer_name'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.crmPriority) {
                    list.push({
                        value: 'crmPriority',
                        label: this.$t('client_priority'),
                        show: true,
                    })
                }
                list.push({
                    value: 'customerPenalty',
                    label: this.$t('client_penalty'),
                    show: true,
                })
                if (this.$conf.public.reservationFilters.waitingApprovalFromMyself) {
                    list.push({
                        value: 'waitingApprovalFromMyself',
                        label: this.$t('waiting_approval_from_myself'),
                        show: true,
                    })
                }
                list.push({
                    value: 'supplier',
                    label: this.$t('supplier_name'),
                    show: true,
                })
                if (this.$conf.public.reservationFilters.channelManagerCode) {
                    list.push({
                        value: 'channelManagerCode',
                        label: this.$t('channel_manager_name'),
                        show: true,
                    })
                }
                if (this.$conf.public.reservationFilters.chmPartnerBookingNumber) {
                    list.push({
                        value: 'chmPartnerBookingNumber',
                        label: this.$t('channel_manager_ref'),
                        show: true,
                    })
                }
                list.push({
                    value: 'supplierPenalty',
                    label: this.$t('supplier_penalty'),
                    show: true,
                })
                if (this.showPaymentAndInvoiceStatus) {
                    if (!this.$conf.public.account.paymentScheduler) {
                        list.push({
                            value: 'customerInvoiceStatuses',
                            label: this.$t('client_invoice_status'),
                            show: true,
                        })
                    }
                    list.push({
                        value: 'customerPaymentStatuses',
                        label: this.$t('client_payment_status'),
                        show: true,
                    })
                    list.push({
                        value: 'supplierPaymentStatuses',
                        label: this.$t('supplier_payment_status'),
                        show: true,
                    })
                    if (!this.$conf.public.account.paymentScheduler) {
                        list.push({
                            value: 'supplierInvoiceStatuses',
                            label: this.$t('supplier_invoice_status'),
                            show: true,
                        })
                    }
                }
            }
            if (this.isB2B) {
                list.push({
                    value: 'supplierRef',
                    label: this.$t('supplier_ref'),
                    show: true,
                })
                list.push({
                    value: 'noSupplierRef',
                    label: this.$t('empty_supplier_ref'),
                    show: true,
                })
            }

            if (this.isTO1 || this.isTO2 || this.isAgency) {
                if (this.$conf.public.reservationFilters.agentRefNumber) {
                    list.push({
                        value: 'agentRefNumber',
                        label: this.$t('agentRefNumber'),
                        show: true,
                    })
                }
                list.push({
                    value: 'externalRefNumber',
                    label: this.$t('externalRefNumber'),
                    show: true,
                })
            }

            if (this.hiddenFilters?.filters?.length) {
                this.hiddenFilters.filters.forEach(item => {
                    const index = list.findIndex(el => el.value === item)
                    if (index >= 0) list[index].show = false
                })
            }

            if (this.companyPageMode) {
                const excludedFilters = [
                    'travelerName',
                    'client',
                    'customerCompany',
                    'agent',
                    'clientType',
                    'crmPriority',
                    'customerInvoiceStatuses',
                    'customerPaymentStatuses',
                    'customerPenalty',
                    'paymentMethod',
                    'tripOrganizer',
                    'corporatePolicyViolated',
                    'waitAuthorize',
                    'waitingApprovalFromMyself',
                    'agentCompanyConsortium',
                    'agentCompanySubconsortium',
                    'agencyRegionCode',
                    'customerPaymentDueDate',
                ]
                list = list.filter(item => !excludedFilters.includes(item.value))
            }

            return list
        }

        get tableColumns() {
            const tableColumns: TableHeadersType = [
                {
                    title: this.$t('statement_of_account.refNumber'),
                    value: 'id',
                    show: true,
                    key: 'data-table-group',
                    fixed: true,
                },
            ]

            if (this.isTO1) {
                tableColumns.push({
                    title: this.$t('client'),
                    value: 'client',
                    show: true,
                })
            }

            tableColumns.push(
                this.isShowOrderStatusByServicesTA
                    ? {title: this.$t('status_by_services'), value: 'statusByServices', show: true}
                    : {title: this.$t('status'), value: 'status', show: true}
            )
            if (this.isTO1) {
                tableColumns.push({title: this.$t('manager'), value: 'manager', show: true})
            }
            tableColumns.push({title: this.$t('order_service'), value: 'title', show: true})
            tableColumns.push({title: this.$t('dates'), value: 'date', show: true, align: 'center'})
            tableColumns.push({title: this.$t('order_travelers'), value: 'travellers', show: true})
            if (this.personRole !== 'dispatcher') {
                if (this.isAgency) {
                    tableColumns.push({
                        title: this.$t('price'),
                        value: 'supplierPrice',
                        show: true,
                        align: 'end',
                    })
                } else if (this.isTourOperator && this.commissionAndNettoCheckbox) {
                    tableColumns.push({
                        title: this.$t('supplier_price'),
                        value: 'supplierPrice',
                        show: true,
                        align: 'end',
                    })
                }
            }

            if (
                !this.isAgency &&
                this.personRole !== 'dispatcher' &&
                !this.$conf.public.account.licenseeForCorporateCompany
            ) {
                tableColumns.push({
                    title: this.$t(this.isTourOperator ? 'client_price' : 'price'),
                    value: 'price',
                    show: true,
                    align: 'end',
                })
            }
            if (this.isShowDueToPay) {
                tableColumns.push({
                    title: this.isAgency ? this.$t('due_to_pay') : this.$t('due_to_pay_from_client'),
                    value: 'dueToPay',
                    show: true,
                    align: 'end',
                })
            }
            if (
                !this.isB2C &&
                !this.isCorporate &&
                !this.isSupplier &&
                this.commissionAndNettoCheckbox &&
                !this.$conf.public.account.licenseeForCorporateCompany
            ) {
                tableColumns.push({title: this.$t('income'), value: 'income', show: true, align: 'end'})
            }
            tableColumns.push({title: this.$t('created'), value: 'created', show: true})
            tableColumns.push({title: this.$t('modified'), value: 'modified', show: true})
            if (this.$conf.public.account.showBookingDate && !this.isB2C) {
                tableColumns.push({
                    title: this.$t('booking_date'),
                    value: 'manualBookingDate',
                    show: true,
                })
            }
            if (this.showPaymentAndInvoiceStatus && this.$conf.public.account.showOrderInvoiceBtn) {
                tableColumns.push({
                    title: this.$t('invoices.invoice'),
                    value: 'invoice',
                    show: true,
                    align: 'center',
                })
            }
            tableColumns.push({title: this.$t('voucher'), value: 'voucher', show: true, align: 'center'})

            if (this.hiddenColumns?.columns?.length) {
                this.hiddenColumns.columns.forEach(item => {
                    const index = tableColumns.findIndex(el => el.value === item)
                    if (index >= 0) tableColumns[index].show = false
                })
            }

            if (this.isDefaultTableColumns && this.isB2C) {
                tableColumns.forEach(column => {
                    if (column.value === 'created') {
                        column.show = false
                    }
                    if (column.value === 'modified') {
                        column.show = false
                    }
                    if (column.value === 'invoice') {
                        column.show = false
                    }

                    return column
                })
            }

            return tableColumns
        }

        get isShowNewReservationRequest() {
            return this.$conf.public.account.newReservationRequest
        }

        get isDefaultTableColumns() {
            return !this.hiddenColumns.userId
        }

        get tabs() {
            return [
                {icon: 'mdi-format-list-text', label: 'reservations'},
                {icon: 'mdi-tune', label: 'filters'},
            ]
        }

        get selectable() {
            return authStore.isTourOperator || authStore.isAgency
        }

        get ordersCount() {
            const num = runtimeStore.ordersResponse.ordersCount || 0

            return new Intl.NumberFormat().format(num)
        }

        get servicesCount() {
            const num = runtimeStore.ordersResponse.servicesCount || 0

            return new Intl.NumberFormat().format(num)
        }

        get filterKey() {
            return runtimeStore.ordersResponse.filterKey || ''
        }

        get isB2B() {
            return authStore.isB2B
        }

        get isB2C() {
            return authStore.isB2C
        }

        get isTourOperator() {
            return authStore.isTourOperator
        }

        get isAgency() {
            return authStore.isAgency
        }

        get isTO1() {
            return authStore.isTO1
        }

        get isTO2() {
            return authStore.isTO2
        }

        get isCorporate() {
            return authStore.isCorporate
        }

        get isSupplier() {
            return authStore.isSupplier
        }

        get person() {
            return authStore.person
        }

        get personRole() {
            return authStore.personRole
        }

        get isShowDueToPay() {
            return (this.isTourOperator || this.isAgency) && this.personRole !== 'dispatcher'
        }

        get isShowOrderStatusByServicesTA() {
            return this.$conf.public.account.showOrderStatusByServicesTA && (this.isAgency || this.isTO2)
        }

        get showPaymentAndInvoiceStatus() {
            return authStore.showPaymentAndInvoiceStatus && this.personRole !== 'dispatcher'
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }

        get commissionAndNettoCheckbox() {
            return persistentStore.commissionAndNettoCheckbox
        }

        get optionOrdersEnabled() {
            return runtimeStore.ordersResponse.orders.some(order =>
                order.services.find(
                    service =>
                        service.status.toLowerCase() === 'preconfirmed' ||
                        service.status.toLowerCase() === 'pre_confirmed'
                )
            )
        }

        get showCancelledServicesCheckbox() {
            if (this.personRole === 'dispatcher') return false

            return (
                runtimeStore.ordersResponse.orders.some(
                    order =>
                        order.services.findIndex(
                            service =>
                                service.status.toLowerCase() === 'canceled' &&
                                service.salesTerms.find(item => item.type === 'CLIENT').price.amount === 0
                        ) >= 0
                ) ||
                runtimeStore.ordersResponse.orders.some(
                    order => order.services.findIndex(service => service.status.toLowerCase() === 'rejected') >= 0
                )
            )
        }

        get showOrderEmailsMenu() {
            return true
        }

        get isShowReservationsSummaryModal() {
            return (this.isTO1 || this.isTO2 || this.isAgency || this.isCorporate) && this.filterKey
        }
    }

    export default OrdersPage
</script>
